/**
 * LES SUPPORTS DE MAINTENANCE, côté écran — module PUR (spec 002, récit 2,
 * 04/11/2026).
 *
 * Le serveur RÉSOUT les supports d'une échéance (`supportsDeLEcheance` :
 * supports de la ligne ∪ vidéos et notices de l'appareil reliées à aucune
 * ligne). Ce fichier ne fait que choisir où les lire et les dire en mots ; il
 * est pur pour être testé sans écran (`front-supports-maintenance`).
 */
import type { MaintenanceLigne, MaintenanceOccurrence, RefSupportMaintenance, SupportEcheance } from './types';

/** Les supports à proposer dans la fenêtre « Déclarer faite » : ceux de la
 *  ligne quand l'écran tient le registre, sinon ceux que l'occurrence porte
 *  (mémo du jour). Jamais « toutes les vidéos de l'appareil ». */
export function supportsDeLaFenetre(
  ligne: Pick<MaintenanceLigne, 'supportsEcheance'> | undefined,
  occ: Pick<MaintenanceOccurrence, 'supports'>,
): SupportEcheance[] {
  if (ligne && Array.isArray(ligne.supportsEcheance)) return ligne.supportsEcheance;
  return Array.isArray(occ.supports) ? occ.supports : [];
}

/** « sert à : … » d'un support, pour l'onglet Documents et l'onglet Maintenance. */
export function direSertA(sertA: string[] | undefined, commun: boolean | undefined): string {
  const l = (sertA || []).filter(Boolean);
  if (l.length) return `sert à : ${l.join(', ')}`;
  return commun ? 'sert à : toutes les maintenances' : 'reliée à aucune maintenance';
}

/** Le type d'un support, en clair. */
export function libelleTypeSupport(s: Pick<SupportEcheance, 'type' | 'nature'>): string {
  if (s.type === 'document') return 'Document GED';
  switch (s.nature) {
    case 'video': return 'Vidéo';
    case 'notice': return 'Notice';
    default: return 'Pièce du dossier';
  }
}

/** Clé stable d'un support relié. */
export function cleRef(s: RefSupportMaintenance): string {
  return s.type === 'piece' ? `piece:${s.id}` : `doc:${s.code}`;
}

/** Ajoute ou retire un support d'une liste (sans doublon), pour le formulaire de ligne. */
export function basculerSupport(liste: RefSupportMaintenance[] | undefined, s: RefSupportMaintenance): RefSupportMaintenance[] {
  const l = Array.isArray(liste) ? liste : [];
  return l.some((x) => cleRef(x) === cleRef(s)) ? l.filter((x) => cleRef(x) !== cleRef(s)) : [...l, s];
}

/** Le document source d'une ligne, en clair : la référence GED, sinon le texte repris « à relier ». */
export function direDocumentSource(
  l: Pick<MaintenanceLigne, 'sourceDocument' | 'sourceDocumentCode' | 'sourceDocumentTitre'>,
): { texte: string; aRelier: boolean } {
  const code = (l.sourceDocumentCode || '').trim();
  if (code) return { texte: l.sourceDocumentTitre ? `${code} — ${l.sourceDocumentTitre}` : code, aRelier: false };
  const brut = (l.sourceDocument || '').trim();
  return { texte: brut, aRelier: !!brut };
}
